/* ==========================================================================
   Sound Event Detector - Comprehensive Markets i18n Dictionary
   Multi-language articles across EN, CS, DE, ES, AR, PT
   ========================================================================== */

export const marketTranslations = {
  en: {
    healthcare: {
      title: "Acoustic AI Security for Healthcare & Hospitals",
      eyebrow: "HOSPITAL & PATIENT PROTECTION",
      lead: "SED protects healthcare personnel and patients in hospitals and medical facilities. It provides early detection of aggression, panic screams, and acute distress, enabling medical teams and security guards to intervene rapidly.",
      overview: `
        <p>Healthcare facilities face rising verbal aggression and physical altercations in high-stress areas like Emergency Department waiting rooms and triage areas. Traditional CCTV cameras cannot trigger alerts until physical violence is already underway.</p>
        <p>Sound Event Detector provides instant early warning by detecting screaming and verbal panic in patient waiting areas. Because SED processes acoustic signals on the edge without recording human voice, it is 100% compliant with HIPAA and GDPR medical privacy regulations.</p>
      `,
      benefits: [
        "Early aggression detection before physical assault occurs",
        "Zero audio recording — 100% patient medical privacy compliant",
        "Protects ER nurses, doctors, and triage personnel 24/7",
        "Integrates directly into hospital security dispatch desks"
      ]
    },
    education: {
      title: "Acoustic Security & Threat Detection for Education & Campuses",
      eyebrow: "SCHOOL & CAMPUS SAFETY",
      lead: "SED safeguards pupils, students, and educators in primary, secondary, and higher education facilities and university campuses. It promptly alerts to gunshots, panic screams, glass breakage, and other hazardous incidents.",
      overview: `
        <p>Schools, K-12 classrooms, and university campuses require ultra-fast active threat detection to protect students and educators. When seconds count during an active emergency, instant acoustic alert dispatch saves lives.</p>
        <p>SED sensors mounted in hallways, cafeterias, and campus plazas detect gunshot blasts and panic screams in under 5 seconds, automatically triggering security alerts and transmitting precise sound location coordinates—all while respecting student privacy with 100% GDPR zero-audio-recording architecture.</p>
      `,
      benefits: [
        "<5s gunshot & active emergency alert dispatch to emergency services",
        "Detects panic screams & verbal bullying in hallways and sports arenas",
        "Zero audio recording — 100% student privacy & GDPR compliant",
        "Integrates directly with campus access control & PA lockdown systems"
      ]
    },
    industrial: {
      title: "Acoustic Detection for Industry & Critical Infrastructure",
      eyebrow: "PERIMETER & CRITICAL ASSETS",
      lead: "SED provides robust perimeter protection for manufacturing plants, logistics warehouses, power stations, substations, and critical utility sites. It reliably detects fence cutting with angle grinders, gunshots, unauthorized UAVs, and vehicle intrusions.",
      overview: `
        <p>Industrial perimeters stretching over thousands of meters are difficult to monitor using visual cameras alone. Intruders cut fence wiring under cover of fog, darkness, or foliage.</p>
        <p>SED nodes installed along perimeter fences detect angle grinder cutting, fence breach, and vehicle intrusion instantly, automatically slewing PTZ cameras to capture high-definition video evidence.</p>
      `,
      benefits: [
        "Instant detection of perimeter fence cutting & angle grinders",
        "Operates in heavy fog, rain, dust, and total darkness",
        "Direct integration with Axis, Hanwha, Milestone, and Genetec VMS",
        "Reduces false alarms caused by wildlife or wind"
      ]
    },
    public: {
      title: "Acoustic Detection for Cities & Public Spaces",
      eyebrow: "MUNICIPAL & PUBLIC SAFETY",
      lead: "SED protects urban residents and visitors, enhancing safety in public squares, parks, and transit corridors. It recognizes critical safety incidents in real time, allowing emergency response teams to react before situations escalate.",
      overview: `
        <p>By pairing optical camera networks with directional sound event detection, municipal control centers gain instant acoustic situational awareness without invading citizen privacy or recording ambient conversations.</p>
        <p>When emergency sounds occur, PTZ cameras automatically slew to the sound source coordinates and alert dispatchers in under 5 seconds.</p>
      `,
      benefits: [
        "<5s alert dispatch to Municipal Police Command Centers",
        "Automated PTZ camera auto-tracking to sound source",
        "Covers public plazas, transport hubs, parks, and night-life districts",
        "100% GDPR compliant — zero ambient wiretapping"
      ]
    },
    private: {
      title: "Acoustic Detection for Commercial Properties & Private Estates",
      eyebrow: "COMMERCIAL REAL ESTATE & PRIVATE SITES",
      lead: "SED protects shopping centers, corporate headquarters, logistics facilities, hotels, and luxury private estates. It instantly alerts to glass break, aerosol graffiti spraying, unauthorized vehicular entry, and forced burglary attempts.",
      overview: `
        <p>For corporate buildings and high-value private properties, SED provides an invisible perimeter ear that detects glass fracture, forced entry drilling, aerosol spraying, and alarms before intruders cross internal thresholds.</p>
        <p>Integrates natively with perimeter alarm systems, triggering high-decibel strobes and notifying private security teams in milliseconds.</p>
      `,
      benefits: [
        "Discreet aesthetic deployment with zero visual intrusion",
        "Instant alert notifications to private security guards",
        "Protects perimeter gates, underground garages, and glass facades",
        "Prevents false alarms caused by weather or pets"
      ]
    }
  },
  cs: {
    healthcare: {
      title: "Akustická AI bezpečnost pro zdravotnictví a nemocnice",
      eyebrow: "OCHRANA NEMOCNIC A ZDRAVOTNÍKŮ",
      lead: "SED chrání zdravotnický personál i pacienty v nemocnicích a dalších zdravotnických zařízeních. Včas detekuje projevy agrese, panického křiku a dalších rizikových zvuků a umožňuje personálu i bezpečnostní službě rychle reagovat.",
      overview: `
        <p>Zdravotnická zařízení čelí narůstající slovní agresi a fyzickým konfliktům v exponovaných prostorách, jako jsou čekárny urgentních příjmů a triage koridory. Běžné kamerové systémy dokáží varovat až ve chvíli, kdy dochází k fyzickému násilí.</p>
        <p>Sound Event Detector poskytuje okamžité včasné varování detekcí panického křiku a agrese. Protože zpracovává akustické signály na edge čipu bez nahrávání lidské řeči, splňuje 100% GDPR a zdravotnické předpisy pro ochranu soukromí.</p>
      `,
      benefits: [
        "Ověřené nasazení ve Fakultní nemocnici Plzeň (FN Plzeň)",
        "Včasná detekce agrese dříve, než vypukne fyzické napadení personálu",
        "Kombinovaná ochrana vnitřního příjmu i venkovního perimetru sanitek",
        "Nulové nahrávání zvuku – 100% garance soukromí pacientů (GDPR)",
        "Přímá integrace do bezpečnostního velína nemocnice a tiché tísňové hlášení"
      ]
    },
    education: {
      title: "Akustická bezpečnost a detekce střelby pro školství a fakulty",
      eyebrow: "BEZPEČNOST ŠKOL A KAMPUSŮ",
      lead: "SED přispívá k ochraně žáků, studentů a pedagogů v základních, středních i vysokých školách a na univerzitních kampusech. Včas upozorňuje na střelbu, panický křik, tříštění skla i další nebezpečné události a pomáhá minimalizovat následky krizových situací.",
      overview: `
        <p>Školy, učebny a univerzitní kampusy vyžadují bleskovou detekci aktivních hrozeb pro ochranu studentů a pedagogů. Při krizové situaci zachraňuje včasná informace lidské životy.</p>
        <p>Senzory SED umístěné na chodbách, v jídelnách a prostranstvích detekují výstřely a panický křik pod 5s (<5s), spouští automatické uzamčení dveří a předávají souřadnice policii – to vše při 100% GDPR garanci ochrany soukromí studentů.</p>
      `,
      benefits: [
        "Bleskové předání poplachu o střelbě složkám IZS pod 5s (<5s)",
        "Detekce panického křiku a šikany na chodbách a v tělocvičnách",
        "Nulové nahrávání řeči – 100% GDPR ochrana soukromí studentů",
        "Přímé propojení s přístupovými a rozhlasovými systémy školy"
      ]
    },
    industrial: {
      title: "Akustická detekce pro průmysl a kritickou infrastrukturu",
      eyebrow: "OCHRANA PERIMETRU A KRITICKÉ INFRASTRUKTURY",
      lead: "SED zajišťuje ochranu perimetru, výrobních areálů, skladů, elektráren, trafostanic a dalších objektů kritické infrastruktury. Spolehlivě detekuje pokusy o narušení perimetru úhlovou bruskou, výstřely, drony i neoprávněný vjezd vozidel.",
      overview: `
        <p>Rozsáhlé průmyslové perimetry je obtížné monitorovat pouze optickými kamerami. Narušitelé řeží oplocení v mlze, tmě nebo za porostem.</p>
        <p>Nody SED umístěné na plotu okamžitě detekují řezání úhlovou bruskou, shazování plotu i neoprávněný vstup a natáčejí PTZ kamery pro zachycení důkazů.</p>
      `,
      benefits: [
        "Okamžitá detekce řezání plotu a úhlových brusek",
        "Funguje v husté mlze, dešti, prachu i absolutní tmě",
        "Nativní integrace s VMS systémy Axis, Hanwha, Milestone a Genetec",
        "Redukce falešných poplachů způsobených zvěří či větrem"
      ]
    },
    public: {
      title: "Akustická detekce pro města a veřejný prostor",
      eyebrow: "MĚSTA A VEŘEJNÁ BEZPEČNOST",
      lead: "SED chrání obyvatele i návštěvníky měst a zvyšuje bezpečí ve veřejném prostoru. V reálném čase rozpoznává krizové situace a umožňuje složkám integrovaného záchranného systému reagovat dříve, než dojde k eskalaci.",
      overview: `
        <p>Propojením městských kamerových systémů s akustickou detekcí SED získávají dispečinky okamžitý přehled o krizových událostech bez narušení soukromí občanů či záznamu hovorů.</p>
        <p>Systém v reálném čase identifikuje střelbu, panický křik, autonehody i sirény záchranných složek a navádí otočné PTZ kamery přímo na místo incidentu.</p>
      `,
      benefits: [
        "Předání poplachu na dispečink Městské policie a IZS za <5s",
        "Automatické natáčení PTZ kamer na místo zdroje zvuku",
        "Pokrývá náměstí, dopravní uzly, parky i ulice s nočním životem",
        "100% GDPR shoda – nulové nahrávání lidských hovorů"
      ]
    },
    private: {
      title: "Akustická detekce pro komerční objekty a soukromý majetek",
      eyebrow: "SOUKROMÝ A KOMERČNÍ SEKTOR",
      lead: "SED chrání obchodní centra, kancelářské budovy, logistické areály, hotely i soukromé rezidence. Okamžitě upozorňuje na tříštění skla, sprejování graffiti, neoprávněný vjezd i pokusy o vloupání.",
      overview: `
        <p>Pro komerční budovy a privátní nemovitosti poskytuje SED diskrétní perimetrickou ochranu, která detekuje tříštění skla, sprejování graffiti a pokusy o vloupání dříve, než pachatel pronikne do objektu.</p>
        <p>Propojuje se s bezpečnostními systémy a předává poplach bezpečnostní službě v řádu milisekund.</p>
      `,
      benefits: [
        "Diskrétní instalace bez narušení architektury budov",
        "Okamžitá notifikace bezpečnostní službě a ostraze",
        "Chrání vstupy, prosklené fasády, nádvoří a podzemní garáže",
        "Zabraňuje falešným poplachům od povětrnostních vlivů"
      ]
    }
  },
  de: {
    healthcare: {
      title: "Akustische KI-Sicherheit für Gesundheitswesen & Krankenhäuser",
      eyebrow: "KRANKENHAUS- UND PATIENTENSCHUTZ",
      lead: "SED schützt medizinisches Personal und Patienten in Krankenhäusern durch frühzeitige Erkennung von Aggressionen und Panikschreien.",
      overview: "<p>Frühzeitige Erkennung von Aggressionen in Notaufnahmen ohne Sprachaufzeichnung.</p>",
      benefits: ["Erkennt Aggression vor körperlicher Gewalt", "100% DSGVO-konform"]
    },
    education: {
      title: "Akustische Sicherheit für Schulen & Universitäten",
      eyebrow: "SCHUL- UND CAMPUS-SICHERHEIT",
      lead: "SED schützt Schüler und Lehrkräfte durch sofortige Erkennung von Schüssen und Panikschreien in Echtzeit.",
      overview: "<p>Schnelle Schusserkennung und Panikmeldung für Bildungseinrichtungen.</p>",
      benefits: ["Alarmierung in unter <5s", "100% DSGVO-konform"]
    },
    industrial: {
      title: "Akustische Detektion für Industrie & Kritische Infrastruktur",
      eyebrow: "INDUSTRIE & INFRASTRUKTUR",
      lead: "SED schützt Industrieareale, Kraftwerke und Solarparks vor Sabotage und Zauneinbrüchen.",
      overview: "<p>Zuverlässiger Perimeterschutz für Industrieanlagen und Versorger.</p>",
      benefits: ["Erkennt Trennschleifer und Zaunschnitt", "Funktioniert bei Nebel und Dunkelheit"]
    },
    public: {
      title: "Akustische Detektion für Städte & Öffentlichen Raum",
      eyebrow: "STÄDTE & ÖFFENTLICHE SICHERHEIT",
      lead: "SED erhöht die Sicherheit im öffentlichen Raum und unterstützt Polizeileitstellen in Echtzeit.",
      overview: "<p>Echtzeit-Akustik für moderne Städte ohne Sprachüberwachung.</p>",
      benefits: ["Alarmierung in <5s", "DSGVO-konform ohne Sprachspeicherung"]
    },
    private: {
      title: "Akustische Detektion für Gewerbe & Private Liegenschaften",
      eyebrow: "GEWERBE & PRIVATER SEKTOR",
      lead: "SED schützt Einkaufszentren, Bürogebäude und Residenzen vor Glasbruch und Einbruch.",
      overview: "<p>Akustischer Schutz für Gewerbeobjekte und Privatimmobilien.</p>",
      benefits: ["Diskrete Montage", "Sofortige Benachrichtigung der Security"]
    }
  },
  es: {
    healthcare: {
      title: "Seguridad Acústica con IA para Hospitales y Salud",
      eyebrow: "PROTECCIÓN HOSPITALARIA Y PACIENTES",
      lead: "SED protege al personal sanitario y pacientes en hospitales detectando agresiones y gritos de pánico a tiempo.",
      overview: "<p>Detección temprana de agresiones en urgencias sin grabación de voz.</p>",
      benefits: ["Detección de agresión antes de violencia física", "100% compatible con RGPD"]
    },
    education: {
      title: "Seguridad Acústica para Escuelas y Campus",
      eyebrow: "SEGURIDAD EN ESCUELAS Y CAMPUS",
      lead: "SED protege a alumnos y docentes alertando sobre disparos, gritos de pánico y rotura de cristales.",
      overview: "<p>Detección ultrarrápida de amenazas activas en entornos educativos.</p>",
      benefits: ["Despacho de alerta en <5s", "100% privacidad estudiantil RGPD"]
    },
    industrial: {
      title: "Detección Acústica para Industria e Infraestructuras Críticas",
      eyebrow: "INDUSTRIA E INFRAESTRUCTURA",
      lead: "SED protege plantas industriales, almacenes y subestaciones contra sabotajes e intrusiones.",
      overview: "<p>Detección de corte de vallas y herramientas en perímetros industriales.</p>",
      benefits: ["Detecta radiales y corte de vallas", "Funciona en niebla y oscuridad"]
    },
    public: {
      title: "Detección Acústica para Ciudades y Espacios Públicos",
      eyebrow: "CIUDADES Y SEGURIDAD PÚBLICA",
      lead: "SED protege a ciudadanos en plazas y parques reconociendo incidentes críticos en tiempo real.",
      overview: "<p>Integración acústica para centros de control urbano sin invasión de la privacidad.</p>",
      benefits: ["Alerta en <5s a la policía", "100% compatible con RGPD"]
    },
    private: {
      title: "Detección Acústica para Edificios Comerciales y Propiedades Privadas",
      eyebrow: "SECTOR PRIVADO Y COMERCIAL",
      lead: "SED protege centros comerciales, oficinas y residencias contra rotura de cristales y robos.",
      overview: "<p>Protección perimetral invisible para empresas y propiedades privadas.</p>",
      benefits: ["Instalación discreta", "Alerta inmediata a guardias"]
    }
  },
  pt: {
    healthcare: {
      title: "Segurança Acústica com IA para Saúde e Hospitais",
      eyebrow: "PROTEÇÃO HOSPITALAR E PACIENTES",
      lead: "O SED protege o pessoal médico e pacientes detetando precocemente agressões verbais e gritos de pânico.",
      overview: "<p>Deteção precoce de agressões em serviços de urgência sem gravação de voz.</p>",
      benefits: ["Deteção de agressão antes de violência física", "100% compatível com RGPD"]
    },
    education: {
      title: "Segurança Acústica para Escolas e Universidades",
      eyebrow: "SEGURANÇA EM ESCOLAS E CAMPI",
      lead: "O SED salvaguarda estudantes e professores alertando sobre disparos, gritos de pânico e quebra de vidros.",
      overview: "<p>Deteção ultrarrápida de incidentes em estabelecimentos de ensino.</p>",
      benefits: ["Alerta em <5s às forças de segurança", "100% privacidade estudantil RGPD"]
    },
    industrial: {
      title: "Deteção Acústica para Indústria e Infraestruturas Críticas",
      eyebrow: "INDÚSTRIA E INFRAESTRUTURAS",
      lead: "O SED garante a segurança perimetral de fábricas, armazéns e centrais de energia.",
      overview: "<p>Deteção de corte de vedações e ferramentas pesadas em perímetros industriais.</p>",
      benefits: ["Deteta rebarbadoras e corte de vedações", "Funciona com nevoeiro e escuridão"]
    },
    public: {
      title: "Deteção Acústica para Cidades e Espaço Público",
      eyebrow: "CIDADES E SEGURANÇA PÚBLICA",
      lead: "O SED reforça a segurança urbana detetando situações de emergência em tempo real.",
      overview: "<p>Monitorização acústica para centros de comando municipal com total privacidade.</p>",
      benefits: ["Alerta em <5s à polícia municipal", "100% compatível com RGPD"]
    },
    private: {
      title: "Deteção Acústica para Edifícios Comerciais e Propriedade Privada",
      eyebrow: "SETOR PRIVADO E COMERCIAL",
      lead: "O SED protege centros comerciais, edifícios de escritórios e residências contra assaltos.",
      overview: "<p>Proteção perimetral discreta para empresas e propriedades privadas.</p>",
      benefits: ["Instalação discreta", "Alerta imediato às equipas de segurança"]
    }
  },
  ar: {
    healthcare: {
      title: "الأمان الصوتي بالذكاء الاصطناعي للمستشفيات والقطاع الصحي",
      eyebrow: "حماية المستشفيات والكوادر الطبية",
      lead: "يحمي SED الكوادر الطبية والمرضى في المستشفيات بالكشف المبكر عن علامات الاعتداء والصراخ والذعر.",
      overview: "<p>كشف فوري للانفعالات والصراخ في غرف الانتظار دون تسجيل محادثات.</p>",
      benefits: ["كشف الاعتداء قبل وقوعه", "حماية كاملة للخصوصية 100%"]
    },
    education: {
      title: "الأمان الصوتي للمدارس والجامعات",
      eyebrow: "سلامة المدارس والحرم الجامعي",
      lead: "يساهم SED في حماية الطلاب والمعلمين بالتحذير الفوري من إطلاق النار وصراخ الذعر وتكسير الزجاج.",
      overview: "<p>كشف فوري للأحداث الطارئة في المدارس والجامعات مع احترام الخصوصية.</p>",
      benefits: ["إرسال التنبيه في أقل من 5 ثوانٍ", "100% حماية خصوصية الطلاب"]
    },
    industrial: {
      title: "الكشف الصوتي للصناعة والبنية التحتية الحيوية",
      eyebrow: "الصناعة والبنية التحتية",
      lead: "يوفر SED حماية محيطية لمنشآت التصنيع والمستودعات ومحطات الطاقة والمرافق الحيوية.",
      overview: "<p>كشف محاولات اختراق الأسوار واستخدام قواطع المعادن في المنشآت الصناعية.</p>",
      benefits: ["كشف قطع الأسوار الفوري", "يعمل بكفاءة في الضباب والظلام"]
    },
    public: {
      title: "الكشف الصوتي للمدن والأماكن العامة",
      eyebrow: "المدن والأمن العام",
      lead: "يحمي SED سكان المدن وزوارها ويعزز السلامة في الساحات العامة والحدائق وممرات النقل.",
      overview: "<p>ربط كاميرات المدن بالكشف الصوتي لتوفير وعي فوري دون انتهاك الخصوصية.</p>",
      benefits: ["إرسال الإنذار في أقل من 5 ثوانٍ", "100% متوافق مع معايير الخصوصية"]
    },
    private: {
      title: "الكشف الصوتي للمباني التجارية والملكية الخاصة",
      eyebrow: "القطاع التجاري والخاص",
      lead: "يحمي SED المراكز التجارية والمباني الإدارية والفنادق والمجمعات السكنية الخاصة.",
      overview: "<p>حماية غير مرئية لكشف تكسير الزجاج ورذاذ الجرافيتي ومحاولات السطو.</p>",
      benefits: ["تركيب سري وغير مزعج", "إخطار فوري لأفراد الأمن"]
    }
  }
};
